import { Transition } from "@headlessui/react";
import { DocumentTextIcon, FilterIcon, XIcon } from "@heroicons/react/outline";
import { useContext } from "react";
import { createPortal } from "react-dom";
import { useModal, WorkflowContext } from "../contexts";
import WorkflowSelectModal from "./WorkflowSelectModal";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";
import { BLOCK_ICON_MAP } from "../utils";
import WorkflowSourceConditionModal from "./WorkflowSourceConditionModal";
import { useHistory, useRouteMatch } from "react-router-dom";
import useUser from "../hooks/useUser";
import { OAuthError, postData } from "../backend";
import OAuthModalCopper from "./OAuthModalCopper";
import LoadingModal from "./LoadingModal";
import { Trans, useTranslation } from "react-i18next";

function WorkflowSourceCopper() {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const { hasPermission } = useUser();

  const { user, refreshUser } = useUser();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const history = useHistory();
  const { url } = useRouteMatch();

  const { sourceSettingsPortalNode: portalTarget } = useContext(WorkflowContext);

  const source = workflow.source;

  async function openCopperModal() {
    openModal(<LoadingModal />)

    postData("/copper/access-token/")
      .then(() => { refreshUser(); closeModal(); })
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModalCopper oauthUrl={error.oauthUrl} onClose={refreshUser} />);
        else
          console.error(error)
      });
  }

  if (!source)
    return null;

  const showSourceOptions = workflow.documents.length > 0 || workflow.emails.length > 0;

  return (
    <>
      {portalTarget && hasPermission(workflow.team, "workflows.edit") &&
        createPortal(
          <>
            {showSourceOptions && <>
              <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} disableCopy />

              <div className="flex flex-col">
                <div className="flex items-center font-gilroy font-semibold text-gray-600">
                  <FilterIcon className="w-4 h-4 mr-2" /> {t("Source Filter Conditions")}
                </div>
                <div className="flex items-center font-gilroy font-semibold text-sm text-gray-400">
                  {t("Only process data that meets certain conditions")}
                </div>
                {(source!.sourceConditions.length > 0) &&
                  <div className="flex items-center font-gilroy font-semibold text-sm text-gray-600">
                    <div><Trans i18nKey="Filtering by <bold>{{count}}</bold> condition" count={source!.sourceConditions.length} components={{ bold: <span className="font-semibold" /> }} /></div>
                  </div>
                }
                <button className="btn btn-white text-sm w-52 mt-2" onClick={() => openModal(<WorkflowSourceConditionModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                  {source!.sourceConditions.length === 0 ? t("Add Conditions") : t("Edit Conditions")}
                </button>
              </div>
            </>}

            {!showSourceOptions && <>
              <div className="flex flex-col gap-4 justify-center">
                <div className="flex items-center">
                  <DocumentTextIcon className="w-6 h-6 mr-2" />
                  <div className="font-gilroy font-semibold text-lg">
                    {t("Connect a Template")}
                  </div>
                </div>
                <div className="flex flex-col gap-2 font-semibold text-gray-600">
                  {t("Create documents and presentations from a template")}
                </div>
                <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Temaplate")} subtitle={t("Create or select a template to merge your data with")} showDocuments workflowContext={workflowContext} />)}>
                  {t("Connect")}
                </button>
              </div>
            </>}
          </>,
          portalTarget
        )
      }

      <Transition.Child className="flex flex-col bg-white w-full m-4 p-4 pt-2 shadow rounded"
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex w-full items-center mb-2">
          <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
            <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP["COPPER"]} alt="Copper Icon" />
          </div>
          <div className="flex flex-col h-full justify-center">
            <div className="flex text-sm font-semibold font-gilroy">
              <div className="truncate max-w-[400px]">
                Copper
              </div>
            </div>
          </div>
          <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        </div>

        <div className="flex flex-col gap-2 items-center justify-center h-full">

          {!user?.copperCredentials?.copperAccessToken &&
            <>
              <div className="font-semibold text-yellow">{t("Please allow Portant to access your data in Copper")}</div>
              <button className="btn btn-blue" onClick={openCopperModal}>
                {t("Connect Copper")}
              </button>
            </>
          }
          {user?.copperCredentials?.copperAccessToken &&
            <>
              <div className="font-semibold text-green">{t("Copper is connected!")}</div>
              <div className="font-semibold text-gray-600">{t("Create documents directly from Copper")}</div>
            </>
          }
        </div>

      </Transition.Child>
    </>
  );

}

export default WorkflowSourceCopper;
